import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";

import {
  api,
  ApiError,
  FundSearchItem,
  FundSearchResponse,
  UserMe,
  WatchlistAddResult,
  WatchlistDetail,
  WatchlistItemEntry,
  WatchlistSummary,
} from "../api";
import { Icon, PageHeader } from "../ui";

const fetcher = <T,>(path: string) => api.get<T>(path);

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Confirm modal ────────────────────────────────────────────

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  variant: "danger" | "warning";
  onConfirm: () => Promise<void>;
}

function ConfirmModal({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await state.onConfirm();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[400px] mx-4 flex flex-col gap-3 p-6 rounded-md bg-card border border-border animate-scale-in">
        <h3 className="text-sm font-semibold text-main">{state.title}</h3>
        <p className="text-xs text-muted">{state.message}</p>
        <div className="flex gap-2 mt-1">
          {state.variant === "danger" ? (
            <button className="btn-danger" disabled={busy} onClick={handleConfirm}>
              {busy ? "Processing..." : state.confirmLabel}
            </button>
          ) : (
            <button className="btn-primary" disabled={busy} onClick={handleConfirm}>
              {busy ? "Processing..." : state.confirmLabel}
            </button>
          )}
          <button className="btn-default" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Watchlists page ─────────────────────────────────────────

export function Watchlists({ user }: { user: UserMe }) {
  const { data, mutate } = useSWR<WatchlistSummary[]>("/watchlists", fetcher);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title="Watchlists"
        description="Group the funds you follow and export their data as CSV."
        action={
          !showCreate && (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              <Icon name="plus" className="w-4 h-4" />
              New watchlist
            </button>
          )
        }
      />

      {showCreate && (
        <CreateWatchlistForm
          onCreated={(wl) => {
            setShowCreate(false);
            setExpandedId(wl.id);
            mutate();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {!data && <p className="text-sm text-muted">Loading...</p>}
      {data && data.length === 0 && !showCreate && (
        <p className="text-sm text-muted">No watchlists yet. Create one to get started.</p>
      )}

      {data && data.length > 0 && (
        <div className="flex flex-col gap-2">
          {data.map((wl) => (
            <WatchlistCard
              key={wl.id}
              summary={wl}
              expanded={expandedId === wl.id}
              onToggle={() => setExpandedId(expandedId === wl.id ? null : wl.id)}
              onConfirm={setConfirm}
              onDeleted={() => {
                if (expandedId === wl.id) setExpandedId(null);
                mutate();
              }}
              onRenamed={() => mutate()}
              user={user}
            />
          ))}
        </div>
      )}

      {data && data.length > 0 && (
        <p className="text-xs text-muted">
          {data.length} watchlist{data.length !== 1 ? "s" : ""}
        </p>
      )}

      {confirm && <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />}
    </section>
  );
}

// ── Create form ─────────────────────────────────────────────

function CreateWatchlistForm({
  onCreated,
  onCancel,
}: {
  onCreated: (wl: WatchlistDetail) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<WatchlistDetail>("/watchlists", { name: name.trim() });
      onCreated(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create watchlist");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3.5 p-6 rounded-lg bg-card border border-border" onSubmit={submit}>
      <h3 className="text-sm font-semibold text-main">New watchlist</h3>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">Name</span>
        <input
          type="text"
          required
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Top performers"
          className="px-3 py-2 rounded-md text-sm font-sans outline-none transition-colors bg-input border border-input-border text-main focus:border-accent"
        />
      </label>
      {error && <p className="text-[13px] text-neg">{error}</p>}
      <div className="flex gap-2 mt-1">
        <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
          {busy ? "Creating..." : "Create"}
        </button>
        <button type="button" className="btn-default" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── Watchlist card ──────────────────────────────────────────

function WatchlistCard({
  summary,
  expanded,
  onToggle,
  onConfirm,
  onDeleted,
  onRenamed,
  user,
}: {
  summary: WatchlistSummary;
  expanded: boolean;
  onToggle: () => void;
  onConfirm: (s: ConfirmState) => void;
  onDeleted: () => void;
  onRenamed: () => void;
  user: UserMe;
}) {
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(summary.name);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);

  const doRename = async () => {
    if (!newName.trim() || newName.trim() === summary.name) {
      setRenaming(false);
      return;
    }
    setRenameError(null);
    setRenameBusy(true);
    try {
      await api.patch(`/watchlists/${summary.id}`, { name: newName.trim() });
      setRenaming(false);
      onRenamed();
    } catch (err: unknown) {
      setRenameError(err instanceof Error ? err.message : "Failed to rename");
    } finally {
      setRenameBusy(false);
    }
  };

  const confirmDelete = useCallback(() => {
    onConfirm({
      title: "Delete watchlist",
      message: `Permanently delete "${summary.name}" and all its items? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        await api.delete(`/watchlists/${summary.id}`);
        onDeleted();
      },
    });
  }, [summary, onConfirm, onDeleted]);

  return (
    <div className="rounded-lg bg-card border border-border">
      <div className="flex items-center justify-between p-4 px-5 max-sm:flex-col max-sm:items-start max-sm:gap-2.5">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggle}>
          {renaming ? (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={100}
                className="px-2 py-1 rounded-md text-sm font-sans outline-none bg-input border border-input-border text-main focus:border-accent w-48"
                onKeyDown={(e) => { if (e.key === "Enter") doRename(); if (e.key === "Escape") setRenaming(false); }}
                autoFocus
              />
              <button className="btn-sm btn-primary" disabled={renameBusy} onClick={doRename}>
                {renameBusy ? "..." : "Save"}
              </button>
              <button className="btn-sm btn-default" onClick={() => setRenaming(false)}>Cancel</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[15px] font-medium">
                <span className="text-main">{summary.name}</span>
                <span className="text-xs text-muted font-normal">
                  {summary.item_count} fund{summary.item_count !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="text-xs text-muted mt-0.5">
                Updated {formatDate(summary.updated_at)}
              </div>
            </>
          )}
          {renameError && <p className="text-[11px] text-neg mt-1">{renameError}</p>}
        </div>
        <div className="flex gap-1.5 ml-3 shrink-0 max-sm:ml-0">
          <button className="btn-sm btn-default" onClick={onToggle}>
            {expanded ? "Collapse" : "View"}
          </button>
          <button className="btn-sm btn-default" onClick={() => { setNewName(summary.name); setRenaming(true); }}>
            Rename
          </button>
          <button className="btn-sm btn-danger" onClick={confirmDelete}>Delete</button>
        </div>
      </div>

      {expanded && (
        <WatchlistDetailView watchlistId={summary.id} user={user} />
      )}
    </div>
  );
}

// ── Watchlist detail (expanded) ─────────────────────────────

function WatchlistDetailView({ watchlistId, user }: { watchlistId: string; user: UserMe }) {
  const { data, mutate } = useSWR<WatchlistDetail>(
    `/watchlists/${watchlistId}`,
    fetcher,
  );
  const [showSearch, setShowSearch] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user.permissions.includes("nh.admin");
  const canExportMeta = user.tier.can_bulk_export || isAdmin;
  const canExportReturns = user.tier.can_bulk_export && (isAdmin || user.permissions.includes("nh.returns.export"));

  const download = (path: string, filename: string) => {
    setError(null);
    api.download(path, filename).catch((e) => {
      setError(e instanceof ApiError ? `Export failed (${e.status}): ${e.message}` : "Export failed");
    });
  };

  const toggleItem = (mid: number, pid: number) => {
    const key = `${mid}:${pid}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    setRemoving(true);
    setError(null);
    try {
      const items = [...selected].map((k) => {
        const [m, p] = k.split(":");
        return { manager_id: Number(m), program_id: Number(p) };
      });
      await api.delete(`/watchlists/${watchlistId}/items`, { items });
      setSelected(new Set());
      mutate();
    } catch (e) {
      setError(e instanceof ApiError ? `Remove failed (${e.status})` : "Remove failed");
    } finally {
      setRemoving(false);
    }
  };

  if (!data) {
    return <div className="px-4 pb-4 text-xs text-muted">Loading...</div>;
  }

  return (
    <div className="border-t border-border px-5 pb-5 pt-4 flex flex-col gap-3 bg-stat rounded-b-lg">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-sm btn-primary" onClick={() => setShowSearch(!showSearch)}>
          {showSearch ? "Close search" : "Add funds"}
        </button>
        {selected.size > 0 && (
          <button className="btn-sm btn-danger" disabled={removing} onClick={removeSelected}>
            {removing ? "Removing..." : `Remove ${selected.size} selected`}
          </button>
        )}
        <div className="flex-1" />
        {canExportMeta && (
          <button
            className="btn-sm btn-default"
            onClick={() => download(`/metadata/export.csv?watchlist_id=${watchlistId}`, "nh_metadata_watchlist.csv")}
          >
            Metadata CSV
          </button>
        )}
        {canExportReturns ? (
          <button
            className="btn-sm btn-default"
            onClick={() => download(`/market-data/timeseries/export.csv?watchlist_id=${watchlistId}`, "nh_timeseries_watchlist.csv")}
          >
            Time series CSV
          </button>
        ) : (
          <span className="text-[11px] text-muted">
            {canExportMeta ? "Time series export requires the nh.returns.export permission" : "CSV export isn't on the Free plan"}
          </span>
        )}
      </div>

      {error && <p className="text-xs text-neg">{error}</p>}

      {/* Fund search panel */}
      {showSearch && (
        <FundSearchPanel
          watchlistId={watchlistId}
          existingItems={data.items}
          onAdded={() => mutate()}
        />
      )}

      {/* Items table */}
      {data.items.length === 0 ? (
        <p className="text-xs text-muted py-2">
          No funds in this watchlist. Use "Add funds" to search and add.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-3 py-2">Manager</th>
                <th className="px-3 py-2">Program</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const key = `${item.manager_id}:${item.program_id}`;
                return (
                  <tr key={key} className="hover:bg-stat transition-colors">
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggleItem(item.manager_id, item.program_id)}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-main">{item.manager ?? item.manager_id}</td>
                    <td className="px-3 py-1.5 text-main">{item.program ?? item.program_id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Fund search panel ───────────────────────────────────────

function FundSearchPanel({
  watchlistId,
  existingItems,
  onAdded,
}: {
  watchlistId: string;
  existingItems: WatchlistItemEntry[];
  onAdded: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FundSearchItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<WatchlistAddResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const existingSet = new Set(existingItems.map((i) => `${i.manager_id}:${i.program_id}`));

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await api.get<FundSearchResponse>(
          `/metadata?search=${encodeURIComponent(query.trim())}&limit=20`
        );
        setResults(resp.items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const toggleResult = (mid: number, pid: number) => {
    const key = `${mid}:${pid}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const addSelected = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    setError(null);
    setAddResult(null);
    try {
      const items = [...selected].map((k) => {
        const [m, p] = k.split(":");
        return { manager_id: Number(m), program_id: Number(p) };
      });
      const result = await api.post<WatchlistAddResult>(`/watchlists/${watchlistId}/items`, { items });
      setAddResult(result);
      setSelected(new Set());
      onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? `Add failed (${e.status})` : "Add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-md bg-card border border-border">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search funds by manager or program name..."
          className="flex-1 px-3 py-2 rounded-md text-sm font-sans outline-none bg-input border border-input-border text-main focus:border-accent"
          autoFocus
        />
        {selected.size > 0 && (
          <button className="btn-sm btn-primary" disabled={adding} onClick={addSelected}>
            {adding ? "Adding..." : `Add ${selected.size}`}
          </button>
        )}
      </div>

      {addResult && (
        <p className="text-xs text-muted">
          Added {addResult.added}
          {addResult.skipped > 0 && `, ${addResult.skipped} already in list`}
          {addResult.rejected > 0 && `, ${addResult.rejected} not accessible`}
        </p>
      )}
      {error && <p className="text-xs text-neg">{error}</p>}

      {searching && <p className="text-xs text-muted">Searching...</p>}

      {results && results.length === 0 && !searching && (
        <p className="text-xs text-muted">No funds found.</p>
      )}

      {results && results.length > 0 && (
        <div className="table-wrap max-h-64 overflow-y-auto">
          <table className="data-table">
            <thead className="sticky top-0">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-3 py-2">Manager</th>
                <th className="px-3 py-2">Program</th>
                <th className="px-3 py-2">Country</th>
              </tr>
            </thead>
            <tbody>
              {results.map((fund) => {
                const key = `${fund.manager_id}:${fund.program_id}`;
                const alreadyAdded = existingSet.has(key);
                return (
                  <tr
                    key={key}
                    className={`hover:bg-stat transition-colors ${alreadyAdded ? "opacity-40" : ""}`}
                  >
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        disabled={alreadyAdded}
                        onChange={() => toggleResult(fund.manager_id, fund.program_id)}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-main">{fund.manager ?? fund.manager_id}</td>
                    <td className="px-3 py-1.5 text-main">{fund.program ?? fund.program_id}</td>
                    <td className="px-3 py-1.5 text-muted">{fund.country ?? "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
